import { Absolute, Box, Button, Select, Flex, Form, Icon, Input, Tag } from '@cowsquare/design';
import { DateInput } from '@cowsquare/design/src/input/DateInput';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React, { useCallback, useState } from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { color } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Form as FinalForm, Field } from 'react-final-form';
import { Section } from '../../../components/Section';
import { getQueryObject } from '../../../utils/getQueryObject';
import { useAdsQuery, usePagesBindingUrl } from '../../../utils/hooks';
import { isBrowser } from '../../../utils/isBrowser';
import navigateWithQuery from '../../../utils/navigate';
import { Advertisements } from '../../Advertisements';
import { MedicalCardListLoading } from '../../Loading';
import Map from '../../Map';
import MedicalCard from '../../MedicalCard';
import MedicalCardList from '../../MedicalCardList';
import { Title } from '../../Title';
import { getDay, format } from 'date-fns/esm/fp';

const PAGE_SIZE = 21;
const convertDataToOptions = R.pipe(
  R.map(x => ({ name: x.name, value: x.id })),
  R.concat([{ name: 'All' }])
);

const DoctorsFilterBox = styled(Box)`
  background-color: ${themeGet('colors.whites.0')};
`;

const MapWrapper = styled(Box)`
  position: relative;
  overflow: hidden;
`;

const AddressList = styled(Flex)`
  display: inline-flex;
  min-width: 100%;
  height: 100%;
`;

const AddressListWrapper = styled(Box)`
  display: inline-block;
  ${color};
`;

const StyledAbsolute = styled(Absolute)`
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledSection = styled(Section)`
  position: relative;
`;

const StyledModeButton = styled(Button)`
  padding: 0 8px;
`;

const StyledCloseButton = styled(Button)`
  margin: ${themeGet('space.4')}px;
  border: 0;
  border-radius: ${themeGet('radii.4')};
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.3);
  background-color: ${themeGet('colors.whites.0')};
  width: 40px;
  height: 40px;
  text-align: center;
`;

const getCardProps = ({ item, uri, query }) => ({
  title: R.pathOr('', ['doctor', 'name'], item),
  variant: 'vertical',
  coverUrl: null,
  linkUrl: `${uri}/${R.pathOr('', ['doctor', 'id'], item)}`,
  tags: R.pipe(
    R.pathOr([], ['doctor', 'tags']),
    R.map(R.path(['tag', 'name'])),
    R.map(tag => (
      <Tag
        key={tag}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          navigateWithQuery(uri, { ...query, tag });
        }}
      >
        {tag}
      </Tag>
    ))
  )(item),
  cardInfoData: [
    {
      icon_name: 'PHONE',
      text: item.phone,
    },
    {
      icon_name: 'LOCATION',
      text: item.address,
    },
  ],
  width: ['auto', 320],
  height: [186, 210],
});

const GET_CLINICS = gql`
  query getClinics($limit: Int, $offset: Int, $where: hkdutest_clinics_bool_exp) {
    government_support: hkdutest_government_programs {
      id
      name
    }

    specialties: hkdutest_recognized_specialties {
      id
      name
    }

    list: hkdutest_clinics(limit: $limit, offset: $offset, where: $where) {
      id
      name
      phone
      address
      address_geo
      doctor {
        id
        name
        tags {
          tag {
            name
          }
        }
      }
    }

    total: hkdutest_clinics_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

const VIEW_MODES = {
  LIST: 'list',
  MAP: 'map',
};

const buildCoordinates = ({ lat, lng }) => `${lat},${lng}`;

const composeDoctorNameSearch = doctorName => state =>
  doctorName
    ? R.assocPath(['doctor', 'name', '_ilike'], `%${doctorName}%`, state)
    : R.dissocPath(['doctor', 'name'], state);

const composeSpecialistSearch = specialist => state =>
  specialist
    ? R.assocPath(['doctor', 'recognized_specialties', 'recognized_specialties_id', '_eq'], specialist, state)
    : R.dissocPath(['doctor', 'recognized_specialties'], state);

const composeGovernmentSupportSearch = governmentProgramId => state =>
  governmentProgramId
    ? R.assocPath(['support_government_program', 'government_program_id', '_eq'], governmentProgramId, state)
    : R.dissoc('support_government_program', state);

const composeServiceSearch = value => state =>
  value
    ? R.assocPath([value, '_eq'], true, state)
    : R.pipe(
        R.dissoc('emergency_service_supported'),
        R.dissoc('out_going_service_supported'),
        R.dissoc('vaccine_service_supported')
      )(state);

const composeDatetimeSearch = value => state => {
  if (!value) {
    return R.dissoc('opening_time', state);
  }
  const day = getDay(value);
  const time = format('HH:mm:ssxxxxx', value);

  return R.pipe(
    R.assocPath(['opening_time', 'day', '_eq'], day),
    R.assocPath(['opening_time', 'start_time', '_lte'], time),
    R.assocPath(['opening_time', 'end_time', '_gt'], time)
  )(state);
};

const DoctorsList = ({ location, uri }) => {
  const query = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(query, uri);
  const { ads } = useAdsQuery(uri);

  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [activedCoordinates, setActivedCoordinates] = useState(null);

  const mode = R.propOr(VIEW_MODES.LIST, 'mode', query);

  const handleAdvancedSearchToggle = useCallback(() => setAdvancedSearchOpen(R.not), []);

  const handleViewModeChange = useCallback(
    mode => {
      navigateWithQuery(uri, { ...query, mode });
    },
    [query, uri]
  );

  const handleMarkerClick = useCallback(({ lat, lng }) => {
    setActivedCoordinates(buildCoordinates({ lat, lng }));
  }, []);
  const [whereClause, setWhereClause] = useState({ doctor: { is_public: { _eq: true } } });

  return (
    <Query
      query={GET_CLINICS}
      variables={{
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        where: whereClause,
      }}
    >
      {({ data, loading }) => {
        const list = R.propOr([], 'list', data);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);
        const markers = R.pipe(
          R.map(clinic => clinic.address_geo),
          R.uniq
        )(list);

        const addressList = R.pipe(
          R.groupBy(clinic => buildCoordinates(clinic.address_geo)),
          R.pathOr([], [activedCoordinates])
        )(list);

        const isMobileMap = isBrowser ? window.innerWidth < 992 && mode === VIEW_MODES.MAP : false;

        return (
          <StyledSection>
            <Helmet>
              <title>Doctors - HKDU Public</title>
            </Helmet>
            <Box display={isMobileMap ? 'none' : 'block'}>
              <Advertisements ads={ads} />
            </Box>
            <Box py={[0, 8]}>
              <Box display={isMobileMap ? 'none' : 'block'} mt={[8, 0]} px={[5, 0]}>
                <Flex justifyContent="space-between">
                  <Title>Doctors</Title>
                  <Box mx={-4}>
                    <StyledModeButton variant="link" onClick={() => handleViewModeChange(VIEW_MODES.LIST)}>
                      <Icon name="LIST" color={mode === VIEW_MODES.LIST ? 'link' : 'icon'} />
                    </StyledModeButton>
                    <StyledModeButton variant="link" onClick={() => handleViewModeChange(VIEW_MODES.MAP)}>
                      <Icon name="MAP" color={mode === VIEW_MODES.MAP ? 'link' : 'icon'} />
                    </StyledModeButton>
                  </Box>
                </Flex>
                <DoctorsFilterBox p={5} mx={[-5, 0]} my={5}>
                  <FinalForm
                    onSubmit={formValues => {
                      setWhereClause(
                        R.pipe(
                          composeDoctorNameSearch(R.path(['doctor_name'], formValues)),
                          composeSpecialistSearch(R.path(['specialist', 'value'], formValues)),
                          composeGovernmentSupportSearch(R.path(['government_support', 'value'], formValues)),
                          composeServiceSearch(R.path(['service', 'value'], formValues)),
                          composeDatetimeSearch(R.path(['opening_datetime'], formValues))
                        )
                      );
                    }}
                    render={({ handleSubmit, submitting }) => (
                      <Form>
                        <Flex flexDirection={['column', 'row']} justifyContent="space-between">
                          <Box flex={1} px={4}>
                            <Field name="location">
                              {({ input, meta }) => (
                                <Form.FormItem label="地區" input={input} meta={meta}>
                                  <Select
                                    {...input}
                                    placeholder="全部"
                                    options={[{ name: 'All', value: '' }]}
                                  />
                                </Form.FormItem>
                              )}
                            </Field>
                          </Box>
                          <Box flex={1} px={4}>
                            <Field name="opening_datetime">
                              {({ input, meta }) => (
                                <Form.FormItem label="Opening Hours" input={input} meta={meta}>
                                  <DateInput {...input} showTimePicker dateFormat="yyyy-MM-dd hh:mm" />
                                </Form.FormItem>
                              )}
                            </Field>
                          </Box>
                          <Box flex={1} px={4}>
                            <Field name="specialist">
                              {({ input, meta }) => (
                                <Form.FormItem label="Specialist" input={input} meta={meta}>
                                  <Select
                                    {...input}
                                    placeholder="選擇專業科目"
                                    options={convertDataToOptions(R.pathOr([], ['specialties'])(data))}
                                  />
                                </Form.FormItem>
                              )}
                            </Field>
                          </Box>
                        </Flex>
                        {advancedSearchOpen && (
                          <Flex flexDirection={['column', 'row']} justifyContent="space-between">
                            <Box flex={1} px={4}>
                              <Field name="doctor_name">
                                {({ input, meta }) => (
                                  <Form.FormItem label="醫生姓名" input={input} meta={meta}>
                                    <Input
                                      {...input}
                                      placeholder="輸入醫生姓名搜尋"
                                      suffix={<Icon name="SEARCH" />}
                                    />
                                  </Form.FormItem>
                                )}
                              </Field>
                            </Box>
                            <Box flex={1} px={4}>
                              <Field name="service">
                                {({ input, meta }) => (
                                  <Form.FormItem label="Service" input={input} meta={meta}>
                                    <Select
                                      {...input}
                                      placeholder="Select Service"
                                      options={[
                                        { name: 'All', value: '' },
                                        { name: 'Emergency Service', value: 'emergency_service_supported' },
                                        { name: 'Out-going Services', value: 'out_going_service_supported' },
                                        { name: 'Vaccine Service', value: 'vaccine_service_supported' },
                                      ]}
                                    />
                                  </Form.FormItem>
                                )}
                              </Field>
                            </Box>
                            <Box flex={1} px={4}>
                              <Field name="government_support">
                                {({ input, meta }) => (
                                  <Form.FormItem input={input} meta={meta} label="Government Support">
                                    <Select
                                      {...input}
                                      placeholder="Select Service"
                                      options={convertDataToOptions(
                                        R.pathOr([], ['government_support'])(data)
                                      )}
                                    />
                                  </Form.FormItem>
                                )}
                              </Field>
                            </Box>
                          </Flex>
                        )}

                        <Box px={4}>
                          <Flex justifyContent="flex-end">
                            <Button
                              type="button"
                              suffixIcon={advancedSearchOpen ? 'ARROW_UP' : 'ARROW_DOWN'}
                              onClick={handleAdvancedSearchToggle}
                            >
                              Advanced Search
                            </Button>
                            <Box ml={4}>
                              <Button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting}
                                variant="primary"
                              >
                                Submit
                              </Button>
                            </Box>
                          </Flex>
                        </Box>
                      </Form>
                    )}
                  />
                </DoctorsFilterBox>
              </Box>
              <Box display={mode === VIEW_MODES.LIST ? 'block' : 'none'} px={[5, 0]}>
                {R.and(R.isEmpty(list), loading) && <MedicalCardListLoading />}
                <MedicalCardList
                  height={174}
                  cardData={list}
                  total={total}
                  uri={uri}
                  page={page}
                  onPageChange={onPageChange}
                  options={item => getCardProps({ item, uri, query })}
                />
              </Box>
              <MapWrapper display={mode === VIEW_MODES.MAP ? 'block' : 'none'}>
                <Map
                  zoom={12}
                  markers={markers}
                  activedCoordinates={activedCoordinates}
                  containerElement={<Box height={[600, 560]} />}
                  onMarkerClick={handleMarkerClick}
                  options={{
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                />
                <StyledAbsolute top={['auto', 0]} bottom={0} right={[0, 'auto']} left={0}>
                  <AddressListWrapper p={['4px', '12px']} backgroundColor={['#f4f5f8', 'transparent']}>
                    <AddressList flexDirection={['row', 'column']}>
                      {addressList.map(item => (
                        <Box p="4px" key={item.id}>
                          <MedicalCard {...getCardProps({ item, uri, query })} coverUrl={null} />
                        </Box>
                      ))}
                    </AddressList>
                  </AddressListWrapper>
                </StyledAbsolute>
                <Box display={isMobileMap ? 'block' : 'none'}>
                  <Absolute top={0} right={0}>
                    <StyledCloseButton variant="link" onClick={() => handleViewModeChange(VIEW_MODES.LIST)}>
                      <Icon name="CLOSE" />
                    </StyledCloseButton>
                  </Absolute>
                </Box>
              </MapWrapper>
            </Box>
          </StyledSection>
        );
      }}
    </Query>
  );
};

export default DoctorsList;
